import React, { createContext, ReactNode, useContext, useEffect, useRef } from "react";
import { SongDetailsAPI } from "../api/songDetails";
import config from "../config";
import useLyrics from "../hooks/useLyrics";
import { CurrentlyPlayingState } from "../types/currentlyPlayingState";

interface BackgroundFetchState {
  isFetching: boolean;
  hasData: boolean;
  currentSongId: string | null;
}

const BackgroundFetchContext = createContext<BackgroundFetchState>({
  isFetching: false,
  hasData: false,
  currentSongId: null
});

interface BackgroundFetchProviderProps {
  children: ReactNode;
  currentlyPlayingSong: CurrentlyPlayingState;
}

export const BackgroundFetchProvider: React.FC<BackgroundFetchProviderProps> = ({
  children,
  currentlyPlayingSong
}) => {
  const lyrics = useLyrics(currentlyPlayingSong);
  const isFetchingRef = useRef(false);
  const lastProcessedSongRef = useRef<string>("");
  const [state, setState] = React.useState<BackgroundFetchState>({
    isFetching: false,
    hasData: false,
    currentSongId: null
  });

  useEffect(() => {
    const fetchCuriosities = async () => {
      // Don't fetch if no song is playing
      if (!currentlyPlayingSong?.currentlyPlayingObject?.item) {
        return;
      }

      const song = currentlyPlayingSong.currentlyPlayingObject.item;
      const artist = song.artists.map(a => a.name).join(", ");
      const title = song.name;
      const album = song.album.name;
      const spotifyId = song.id;

      // Create a unique song identifier
      const songKey = `${artist}-${title}-${album}`;

      // Prevent multiple API calls for the same song
      if (lastProcessedSongRef.current === songKey || isFetchingRef.current) {
        return;
      }

      // Wait for lyrics to be available AND ensure they match the current song
      if (!lyrics?.lyrics?.plainLyrics) {
        return;
      }

      // Check if the lyrics are for the current song
      if (lyrics.currentlyPlayingItemId !== song.id) {
        return;
      }

      isFetchingRef.current = true;
      lastProcessedSongRef.current = songKey;

      setState(prev => ({
        ...prev,
        isFetching: true,
        currentSongId: song.id
      }));

      try {
        const actualLyrics = lyrics.lyrics.plainLyrics;

        // Try to get existing data first
        const response = await fetch(
          `${config.api.root}/api/songdetail?artist=${encodeURIComponent(artist)}&title=${encodeURIComponent(title)}&level=all`
        );

        if (response.ok) {
          // Data already exists in database
          setState(prev => ({
            ...prev,
            isFetching: false,
            hasData: true
          }));
          console.log(`Background fetch: Found existing curiosities for "${title}" by ${artist}`);
          return;
        }

        if (response.status === 404) {
          // No data exists, create new analysis
          console.log(`Background fetch: Creating new curiosities for "${title}" by ${artist}`);

          const details = await SongDetailsAPI.getSongDetails(
            artist,
            title,
            "advanced",
            actualLyrics,
            album,
            spotifyId
          );

          // Validate that we have expressions
          if (
            details &&
            details.expressions &&
            (details.expressions.advanced?.length > 0 ||
              details.expressions.intermediate?.length > 0 ||
              details.expressions.beginner?.length > 0)
          ) {
            setState(prev => ({
              ...prev,
              isFetching: false,
              hasData: true
            }));
            console.log(
              `Background fetch: Successfully created curiosities for "${title}" by ${artist}`
            );
          } else {
            setState(prev => ({
              ...prev,
              isFetching: false,
              hasData: false
            }));
          }
        } else {
          throw new Error(`Unexpected response: ${response.status}`);
        }
      } catch (error) {
        console.error("Background fetch error:", error);
        setState(prev => ({
          ...prev,
          isFetching: false,
          hasData: false
        }));
      } finally {
        isFetchingRef.current = false;
      }
    };

    // Only fetch if we have a valid song and lyrics
    if (
      currentlyPlayingSong?.currentlyPlayingObject?.item &&
      lyrics?.lyrics?.plainLyrics &&
      lyrics.currentlyPlayingItemId === currentlyPlayingSong.currentlyPlayingObject.item.id
    ) {
      fetchCuriosities();
    }
  }, [currentlyPlayingSong, lyrics]);

  // Reset state when song changes
  useEffect(() => {
    const currentSongId = currentlyPlayingSong?.currentlyPlayingObject?.item?.id;

    if (currentSongId && currentSongId !== state.currentSongId) {
      setState(prev => ({
        ...prev,
        hasData: false,
        currentSongId: currentSongId
      }));
      lastProcessedSongRef.current = "";
      isFetchingRef.current = false;
    }
  }, [currentlyPlayingSong?.currentlyPlayingObject?.item?.id, state.currentSongId]);

  return (
    <BackgroundFetchContext.Provider value={state}>{children}</BackgroundFetchContext.Provider>
  );
};

export const useBackgroundFetch = () => {
  return useContext(BackgroundFetchContext);
};
